// Halaman render animatic (motion graphic dari engine kota Jawsel) — dipakai headless oleh backend/animatic/render.py.
// /animatic?naskah=<id> membaca /animatic/naskah/<id>.timeline.json (dibuat render.py), lalu menyediakan
// window.__animatic.tick(t) yang menggambar frame deterministik pada detik t (jam virtual).
import { useEffect, useRef } from "react";
import { createEngine, emptyState } from "../lib/engine";
import { api } from "../lib/api";
import { pasangJamVirtual, ease } from "../animatic/jamVirtual";
import { buatOverlay } from "../animatic/overlay";
import { gambarPakGub } from "../animatic/pakGub";
import { gambarRusa, gambarWarga } from "../animatic/figuran";

const W = 1080, H = 1920;

// Hutan belantara: pulau penuh hutan_lebat 8x8 (7x7 blok) + pepohonan di tepi
function stateHutan() {
  const s = emptyState();
  s.buildings = [];
  for (let gy = 2; gy + 8 <= 58; gy += 8) for (let gx = 2; gx + 8 <= 58; gx += 8) s.buildings.push({ bid: "hutan_lebat", x: gx, y: gy });
  for (let i = 0; i < 60; i += 2) { s.buildings.push({ bid: "pohon_4", x: i, y: 0 }); s.buildings.push({ bid: "pohon_4", x: 0, y: i }); }
  return s;
}

const lerp = (a, b, p) => a + (b - a) * p;
const ALAM = new Set(["pohon", "pohon_cemara", "hutan_mini", "pohon_4", "pohon_kecil", "palem", "hutan_lebat", "batu_kecil", "kolam", "danau_kecil", "semak", "bunga_kecil"]);

// Tetapkan waktu muncul (detik absolut) untuk tiap elemen kota berdasarkan tahapan `ungkap` di timeline.
// Tahap: {scene, dari, sampai, bid:[..] | jenis:"alam" | jalan:true | runways:true | sisa:true}. Tanpa `ungkap` → semua tampil sejak awal.
function jadwalUngkap(state, tl) {
  const b = state.buildings.map(x => ({ ...x, tMuncul: -1e9 }));
  const roads = (state.roads || []).map(k => ({ k, tMuncul: -1e9 }));
  const runways = (state.runways || []).map(k => ({ k, tMuncul: -1e9 }));
  if (!tl.ungkap) return { b, roads, runways };
  roads.forEach(r => { r.tMuncul = 1e9; }); runways.forEach(r => { r.tMuncul = 1e9; }); // ada tahapan → jalan/landasan tampil hanya bila dijadwalkan
  const jarak = (x, y) => Math.hypot(x - 28, y - 28);
  const sebar = (daftar, t0, t1) => { daftar.sort((p, q) => jarak(p.x ?? +p.k.split(",")[0], p.y ?? +p.k.split(",")[1]) - jarak(q.x ?? +q.k.split(",")[0], q.y ?? +q.k.split(",")[1])); daftar.forEach((it, i) => { it.tMuncul = t0 + (t1 - t0) * (daftar.length > 1 ? i / (daftar.length - 1) : 0); }); };
  const sudah = new Set();
  for (const u of tl.ungkap) {
    const sc = tl.scenes.find(x => x.id === u.scene); const t0 = sc.mulai + (u.dari ?? 0), t1 = sc.mulai + (u.sampai ?? u.dari ?? 0);
    if (u.jalan) sebar(roads, t0, t1);
    else if (u.runways) sebar(runways, t0, t1);
    else { const cocok = b.filter((x, i) => !sudah.has(i) && (u.sisa || (u.jenis === "alam" ? ALAM.has(x.bid) : u.bid.includes(x.bid)))); cocok.forEach(x => sudah.add(b.indexOf(x))); sebar(cocok, t0, t1); }
  }
  b.forEach((x, i) => { if (!sudah.has(i)) x.tMuncul = 1e9; });
  // Hutan lebat perawan menutupi seluruh pulau sejak awal, lalu "ditebang" bergelombang dari pusat saat kota mulai dibangun:
  // tl.hutan = {scene, dari, sampai} → blok hutan_lebat 8x8 di tiap sel yang tidak menyentuh air, hilang antara t0..t1.
  if (tl.hutan) {
    const sc = tl.scenes.find(x => x.id === tl.hutan.scene); const t0 = sc.mulai + (tl.hutan.dari ?? 0), t1 = sc.mulai + (tl.hutan.sampai ?? tl.hutan.dari ?? 0);
    const air = new Set(state.water || []);
    const blok = [];
    for (let gy = 1; gy + 8 <= 59; gy += 8) for (let gx = 1; gx + 8 <= 59; gx += 8) {
      let basah = false;
      for (let dy = 0; dy < 8 && !basah; dy++) for (let dx = 0; dx < 8; dx++) if (air.has(`${gx + dx},${gy + dy}`)) { basah = true; break; }
      const kec = tl.hutan.kecuali; // [[x0,y0],[x1,y1]] area yang dibiarkan terbuka (mis. sekitar Balai Kota)
      const tabrak = kec && gx <= kec[1][0] && gx + 8 > kec[0][0] && gy <= kec[1][1] && gy + 8 > kec[0][1];
      if (!basah && !tabrak) blok.push({ bid: "hutan_lebat", x: gx, y: gy, tMuncul: -1e9, tHilang: 0 });
    }
    blok.sort((p, q) => jarak(p.x + 4, p.y + 4) - jarak(q.x + 4, q.y + 4)).forEach((it, i) => { it.tHilang = t0 + (t1 - t0) * (blok.length > 1 ? i / (blok.length - 1) : 0); });
    b.push(...blok);
  }
  return { b, roads, runways };
}

export default function Animatic() {
  const kanvasRef = useRef(null), ovKanvasRef = useRef(null), ovRef = useRef(null);
  const pratinjau = new URLSearchParams(window.location.search).get("pratinjau");
  useEffect(() => {
    if (pratinjau !== "pakgub") return;
    // lembar karakter: 5 pose + ekspresi di latar biru Jawsel (untuk review desain)
    const c = ovKanvasRef.current, ctx = c.getContext("2d");
    ctx.fillStyle = "#0B1A5C"; ctx.fillRect(0, 0, W, H);
    const pose = [["idle", "netral", false], ["bingung", "kaget", false], ["lambai", "senyum", false], ["tunjuk", "netral", true], ["megafon", "senyum", true], ["lari", "kaget", false]];
    if (new URLSearchParams(window.location.search).get("figuran")) { // lembar figuran menimpa baris kedua
      ctx.fillStyle = "#0B1A5C"; ctx.fillRect(0, 1000, W, 920);
      ctx.save(); ctx.translate(260, 1700); ctx.scale(0.6, 0.6); gambarRusa(ctx, 0.4, { gerak: "jalan" }); ctx.restore();
      ctx.save(); ctx.translate(620, 1700); ctx.scale(0.6, 0.6); gambarWarga(ctx, 0.4, { gerak: "jalan", varian: 1 }); ctx.restore();
      ctx.save(); ctx.translate(880, 1700); ctx.scale(0.6, 0.6); gambarWarga(ctx, 0.4, { gerak: "lambai", varian: 2 }); ctx.restore();
    }
    pose.forEach(([p, e, b], i) => {
      ctx.save(); ctx.translate(180 + (i % 3) * 360, i < 3 ? 820 : 1700); const s = 0.72; ctx.scale(s, s);
      gambarPakGub(ctx, 0.8 + i, { pose: p, ekspresi: e, bicara: b }); ctx.restore();
      ctx.fillStyle = "#9DB4FF"; ctx.font = "600 30px 'Plus Jakarta Sans', sans-serif"; ctx.textAlign = "center"; ctx.fillText(`${p} · ${e}${b ? " · bicara" : ""}`, 180 + (i % 3) * 360, (i < 3 ? 820 : 1700) + 50);
    });
    window.__animatic = { ready: true };
  }, [pratinjau]);
  useEffect(() => {
    if (pratinjau) return undefined;
    const id = new URLSearchParams(window.location.search).get("naskah") || "babak1";
    let aktif = true;
    (async () => {
      window.__animatic = { tahap: "muat" };
      const jam = pasangJamVirtual();
      const [tl, cat] = await Promise.all([fetch(`/animatic/naskah/${id}.timeline.json?${Date.now()}`).then(r => r.json()), api.catalog()]);
      const defs = cat.buildings || [];
      const state = tl.kota === "hutan" ? stateHutan() : ((await api.publicCity(tl.kota)).state || emptyState());
      if (!aktif) return;
      const jadwal = jadwalUngkap(state, tl);
      const ukuran = Object.fromEntries(defs.map(d => [d.bid, [d.w || 1, d.h || 1]]));
      window.__animatic.tahap = "engine";
      const E = createEngine(kanvasRef.current);
      E.setHud(false); E.setSkyMode(tl.langit || "night"); E.setAnimasi(true);
      const ov = buatOverlay(ovRef.current, ovKanvasRef.current);
      ov.praMuat(tl.scenes);
      const srcs = [...new Set(tl.scenes.flatMap(s => (s.elemen || []).map(e => e.src).filter(Boolean)))];
      await Promise.all(srcs.map(src => new Promise(res => { const im = new Image(); im.onload = im.onerror = res; im.src = src; })));

      function tick(t, bangunUlang = true) {
        jam.set(t);
        const sc = tl.scenes.find(s => t >= s.mulai && t < s.mulai + s.durasi) || tl.scenes[tl.scenes.length - 1];
        const tLokal = t - sc.mulai;
        const k = sc.kamera;
        if (k) {
          const p = ease.inOut(ease.clamp(tLokal / sc.durasi)), ke = k.ke || k.dari;
          let zoom = lerp(k.dari.zoom, ke.zoom, p), dx = 0, dy = 0;
          // hentak zoom singkat (scene-level `kamera.hentak` atau elemen ber-`hentak` saat masuk) + goyang kamera (`kamera.goyang`)
          const hentak = [...(k.hentak || []), ...(sc.elemen || []).filter(e => e.hentak != null && e.masuk != null).map(e => ({ pada: e.masuk, skala: e.hentak, durasi: e.hentakDurasi }))];
          for (const h of hentak) { const u = tLokal - h.pada, d = h.durasi ?? 0.35; if (u >= 0 && u < d) zoom *= 1 + (h.skala ?? 0.08) * (1 - ease.out(u / d)); }
          const goyang = [...(k.goyang || []), ...(sc.elemen || []).filter(e => e.goyang != null && e.masuk != null).map(e => ({ pada: e.masuk, kuat: e.goyang, durasi: e.goyangDurasi }))];
          for (const g of goyang) { const u = tLokal - g.pada, d = g.durasi ?? 0.5; if (u >= 0 && u < d) { const a = (1 - u / d) * (g.kuat ?? 28); dx += Math.sin(u * 71) * a; dy += Math.cos(u * 97) * a; } }
          E.cam.zoom = zoom;
          E.centerOn(lerp(k.dari.gx, ke.gx, p), lerp(k.dari.gy, ke.gy, p), W / 2 + dx, H * (k.pusatY ?? 0.5) + dy);
        }
        // kota tahap-t: hanya elemen yang sudah "muncul"; objek baru tiap frame → dunia dibangun ulang (tajam)
        const stateT = bangunUlang ? { ...state, buildings: jadwal.b.filter(x => x.tMuncul <= t && (x.tHilang == null || t < x.tHilang)), roads: jadwal.roads.filter(x => x.tMuncul <= t).map(x => x.k), runways: jadwal.runways.filter(x => x.tMuncul <= t).map(x => x.k) } : state;
        E.render(stateT, defs, {});
        jam.jalankan();
        // efek "pop" di bangunan yang baru muncul (<0.6 dtk)
        const pops = jadwal.b.filter(x => t - x.tMuncul >= 0 && t - x.tMuncul < 0.6 && x.tMuncul > -1e8).map(x => { const [w, h] = ukuran[x.bid] || [1, 1]; const [sx, sy] = E.gridToScreen(x.x + w / 2, x.y + h / 2, 0); return { sx, sy, umur: t - x.tMuncul, besar: Math.max(w, h) }; });
        ov.gambar(sc, tLokal, W, H, pops);
      }
      // pra-putar agar atmosfer (malam) & kehidupan kota stabil sebelum frame pertama
      for (let i = 0; i < 110; i++) { window.__animatic.tahap = `praputar ${i}`; tick(-4 + i / 30, false); }
      window.__animatic = { tick, total: tl.total, fps: tl.fps || 30, ready: true };
    })().catch(e => { window.__animatic = { error: String(e) }; });
    return () => { aktif = false; };
  }, []);
  return (
    <div data-testid="animatic-root" style={{ position: "relative", width: W, height: H, overflow: "hidden", background: "#0B1A5C" }}>
      <canvas ref={kanvasRef} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
      <canvas ref={ovKanvasRef} width={W} height={H} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
      <div ref={ovRef} style={{ position: "absolute", left: 0, top: 0, width: W, height: H }} />
    </div>
  );
}
